import { Input, ValidationControlBase } from "@frui.ts/bootstrap";
import { bound } from "@frui.ts/helpers";
import { IBindingProps } from "@frui.ts/views";
import React from "react";

export interface AdvancedInputProps {
  id?: string;
  testId?: string;
  transformation?: Transformation;
  errorMessage?: string;
  disabled?: boolean;
  trim?: boolean;
  disableCopy?: boolean;
  disablePaste?: boolean;
  ignoredCharacters?: RegExp;
  onBlur?: (e: React.FormEvent<any>) => void;
  className?: string;
  autoComplete?: "on" | "off";
  maxLength?: number;
}

export enum Transformation {
  UpperCased,
  LowerCased,
}

export default class AdvancedInput<TTarget> extends ValidationControlBase<TTarget, AdvancedInputProps & IBindingProps<TTarget>> {
  @bound
  protected onInput(e: any) {
    const { transformation, trim = false, ignoredCharacters } = this.props;
    const start = e.target.selectionStart;
    let formattedValue = "" + e.target.value;
    const originalValueLength = formattedValue.length;

    if (trim) {
      formattedValue = formattedValue.trim();
    }

    if (ignoredCharacters) {
      formattedValue = formattedValue.replace(ignoredCharacters, "");
    }

    if (transformation !== undefined) {
      if (transformation === Transformation.UpperCased) {
        formattedValue = formattedValue.toUpperCase();
      } else if (transformation === Transformation.LowerCased) {
        formattedValue.toLowerCase();
      }
    }

    let position = start;
    if (originalValueLength > formattedValue.length) {
      position--;
    }

    e.target.value = formattedValue;
    e.target.setSelectionRange(position, position);
  }

  @bound
  interceptKeys(event: React.KeyboardEvent<any>) {
    const { disableCopy = false, disablePaste = false } = this.props;

    if (!disableCopy && !disablePaste) return;

    event = event || window.event; // IE support
    const keyCode = event.keyCode;

    if (event.altKey) return;

    const ctrlDown = event.ctrlKey || event.metaKey; // Mac support
    // Check for Alt+Gr (http://en.wikipedia.org/wiki/AltGr_key)
    // Check for ctrl+c, v and x
    if ((ctrlDown && (keyCode === 67 || keyCode === 88) && disableCopy) || (ctrlDown && keyCode === 86 && disablePaste))
      event.preventDefault();

    // Otherwise allow
    return;
  }

  @bound
  onPaste(event: React.SyntheticEvent<any>) {
    const { disablePaste = false } = this.props;

    if (!disablePaste) return;

    event.preventDefault();
  }

  @bound
  protected renderInner() {
    const { id, testId, disabled, target, property, placeholder, maxLength, errorMessage, className, autoComplete, onBlur } =
      this.props;

    return (
      <Input
        id={id}
        data-testid={testId}
        onBlur={onBlur}
        className={className}
        disabled={disabled}
        target={target}
        property={property}
        maxLength={maxLength}
        onKeyDown={this.interceptKeys}
        onPaste={this.onPaste}
        onInput={this.onInput}
        errorMessage={errorMessage}
        placeholder={placeholder}
        autoComplete={autoComplete}
      />
    );
  }
}
